'use client'

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslations } from '@/hooks/useTranslations'
import { ChevronUp, Loader2 } from 'lucide-react'
import { getPowerSync } from '@/lib/powersync/client'
import { getIntentWorker } from '@/lib/intent-worker'

type PullToRefreshContextValue = {
  register: () => () => void
}

const PullToRefreshContext = createContext<PullToRefreshContextValue | null>(null)

const TRIGGER_DISTANCE = 60
const MAX_DISTANCE = 100

export function PullToRefresh({ children }: { children: React.ReactNode }) {
  const t = useTranslations('common')
  const [pullDistance, setPullDistance] = useState(0)
  const [isPullRefreshing, setIsPullRefreshing] = useState(false)
  const startYRef = useRef(0)
  const disabledCountRef = useRef(0)

  const register = useCallback(() => {
    disabledCountRef.current += 1
    return () => {
      disabledCountRef.current -= 1
      if (disabledCountRef.current < 0) disabledCountRef.current = 0
    }
  }, [])

  const ctxValue = useMemo<PullToRefreshContextValue>(() => ({ register }), [register])

  const handleTouchStart = (e: React.TouchEvent) => {
    startYRef.current = e.touches[0].clientY
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (disabledCountRef.current > 0) return
    const diff = e.touches[0].clientY - startYRef.current
    if (window.scrollY === 0 && diff > 0) {
      setPullDistance(Math.min(diff, MAX_DISTANCE))
      if (diff > TRIGGER_DISTANCE) setIsPullRefreshing(true)
    }
  }

  const handleTouchEnd = () => {
    if (isPullRefreshing && disabledCountRef.current === 0) {
      // Soft refresh. PowerSync data is live — a document reload fetches
      // nothing that sync doesn't already deliver. The old bustReload() full
      // reload tore down PowerSync and rebooted the whole app (~5s per pull,
      // teardown → reconnect → re-render; device lifecycle logs showed every
      // pull as a `doc-load [cb]` cycle), and any top-of-page overscroll
      // triggered it. Nudge pending work instead; the SyncStatusPill surfaces
      // any resulting activity.
      try {
        (getPowerSync() as { triggerCrudUpload?: () => void } | null)?.triggerCrudUpload?.()
      } catch { /* PowerSync not initialized — nothing to flush */ }
      try { getIntentWorker().scheduleTick() } catch { /* worker not started */ }
    }
    setPullDistance(0)
    setIsPullRefreshing(false)
  }

  return (
    <PullToRefreshContext.Provider value={ctxValue}>
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {pullDistance > 0 && (
          <div
            className="fixed top-0 left-0 right-0 z-50 flex justify-center items-center bg-[#00ff83] text-[#001c4d] transition-all"
            style={{ height: `${pullDistance}px`, opacity: pullDistance / MAX_DISTANCE }}
          >
            <div className="flex items-center gap-2">
              {isPullRefreshing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{t('releaseToRefresh')}</span>
                </>
              ) : (
                <>
                  <ChevronUp className="w-5 h-5" />
                  <span>{t('pullToRefresh')}</span>
                </>
              )}
            </div>
          </div>
        )}
        {children}
      </div>
    </PullToRefreshContext.Provider>
  )
}

export function useDisablePullToRefresh(disabled: boolean) {
  const ctx = useContext(PullToRefreshContext)
  useEffect(() => {
    if (!ctx || !disabled) return
    return ctx.register()
  }, [ctx, disabled])
}
